import React, { useEffect, useState, useCallback } from 'react';
import { feedbackAPI } from '../../services/api';
import { TableSkeleton, EmptyState } from '../../components/skeletons';

const S = {
  page: { padding: 24, background: 'var(--cream)', minHeight: '100vh' },
  headerArea: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 },
  title: { fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: 'var(--char)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)' },
  actionBar: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' },
  input: { padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, outline: 'none', background: '#fff' },
  select: { padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, outline: 'none', background: '#fff', cursor: 'pointer' },
  btn: { background: '#4C1D95', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'opacity 0.2s', display: 'flex', alignItems: 'center', gap: 6 },
  btnSecondary: { background: '#fff', color: 'var(--char)', border: '1px solid var(--border)', padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'background-color 0.2s' },
  card: { background: '#fff', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.02)', marginBottom: 20 },
  table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  th: { padding: '14px 20px', background: '#f9fafb', fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--border)' },
  td: { padding: '16px 20px', fontSize: 14, color: 'var(--char)', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' },
  badge: { display: 'inline-block', padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' },
  starRating: { color: '#F5A623', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 },
  iconBtn: { border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', padding: 6, borderRadius: 6, transition: 'background-color 0.2s', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
  modalCard: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 600, padding: 28, boxShadow: 'var(--shadow-xl)', position: 'relative', animation: 'scaleUp 0.2s ease-out' },
  modalTitle: { fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: 'var(--char)', marginBottom: 8 },
  formRow: { marginBottom: 18 },
  label: { fontSize: 12, fontWeight: 700, color: 'var(--char)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'block' },
  historyRow: { padding: '14px 0', borderBottom: '1px solid var(--border)' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', background: '#f9fafb', borderTop: '1px solid var(--border)' }
};

export default function FeedbackManager() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [event, setEvent] = useState('');
  const [status, setStatus] = useState('');

  // Modals
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userHistory, setUserHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Settings State
  const [settings, setSettings] = useState({
    buyerRegDays: 7,
    buyerDemandCount: 3,
    vendorRegDays: 7,
    vendorOfferCount: 5
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Alert/Toast
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchFeedbacks = useCallback(() => {
    setLoading(true);
    feedbackAPI.list({
      page,
      limit: 15,
      search,
      role,
      event,
      status
    })
      .then(res => {
        if (res.data.success) {
          setData(res.data.data);
          setTotal(res.data.total);
        }
      })
      .catch(err => {
        console.error('Failed to fetch feedbacks:', err);
        showToast('error', 'Failed to load feedbacks.');
      })
      .finally(() => setLoading(false));
  }, [page, search, role, event, status]);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  // Load Settings
  const handleOpenSettings = () => {
    setShowSettings(true);
    feedbackAPI.getSettings()
      .then(res => {
        if (res.data.success) {
          setSettings(res.data.data);
        }
      })
      .catch(err => console.error('Failed to load settings:', err));
  };

  const handleSaveSettings = (e) => {
    e.preventDefault();
    setSavingSettings(true);
    feedbackAPI.updateSettings(settings)
      .then(res => {
        if (res.data.success) {
          showToast('success', 'Feedback dynamic triggers updated!');
          setShowSettings(false);
        }
      })
      .catch(err => {
        console.error(err);
        showToast('error', err.response?.data?.message || 'Failed to save settings.');
      })
      .finally(() => setSavingSettings(false));
  };

  // View User History
  const handleViewHistory = (item) => {
    setSelectedUser(item.user);
    setShowHistory(true);
    setLoadingHistory(true);
    feedbackAPI.history(item.userId)
      .then(res => {
        if (res.data.success) {
          setUserHistory(res.data.data);
        }
      })
      .catch(err => {
        console.error(err);
        showToast('error', 'Failed to load feedback history.');
      })
      .finally(() => setLoadingHistory(false));
  };

  // Trigger Resend
  const handleResend = (feedbackId) => {
    feedbackAPI.resend(feedbackId)
      .then(res => {
        if (res.data.success) {
          showToast('success', 'Feedback prompt scheduled to send again!');
          fetchFeedbacks();
          if (showHistory && selectedUser) {
            // refresh history view
            feedbackAPI.history(userHistory[0]?.userId).then(r => setUserHistory(r.data.data));
          }
        }
      })
      .catch(err => {
        console.error(err);
        showToast('error', 'Failed to resend feedback prompt.');
      });
  };

  // Trigger Resend All (filtered remind_later)
  const handleResendAll = () => {
    if (window.confirm('Are you sure you want to resend feedback prompts to all users with postponed feedback matching current filters?')) {
      feedbackAPI.resendAll({ role, event })
        .then(res => {
          if (res.data.success) {
            showToast('success', `Successfully scheduled resend for ${res.data.count} users!`);
            fetchFeedbacks();
          }
        })
        .catch(err => {
          console.error(err);
          showToast('error', 'Failed to reschedule feedback reminders.');
        });
    }
  };

  // Handle role change safely
  const handleRoleChange = (selectedRole) => {
    setRole(selectedRole);
    setPage(1);
    // Reset event if it is not valid for the newly selected role
    if (selectedRole === 'buyer' && event === 'offer_submitted') {
      setEvent('');
    } else if (selectedRole === 'vendor' && event === 'demand_posted') {
      setEvent('');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'submitted':
        return <span style={{ ...S.badge, background: '#D1FAE5', color: '#065F46' }}>Submitted</span>;
      case 'remind_later':
        return <span style={{ ...S.badge, background: '#FEF3C7', color: '#92400E' }}>Reminded</span>;
      default:
        return <span style={{ ...S.badge, background: '#F3F4F6', color: '#374151' }}>Pending</span>;
    }
  };

  const getEventName = (ev) => {
    switch (ev) {
      case 'registration': return 'Registration';
      case 'demand_posted': return 'Demand Post';
      case 'offer_submitted': return 'Offer Sent';
      default: return 'Manual';
    }
  };

  return (
    <div style={S.page}>
      <div style={S.headerArea}>
        <div>
          <div style={S.title}>User Feedbacks</div>
          <div style={S.subtitle}>Monitor customer ratings, comments, and configure dynamic response trigger intervals.</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={S.btnSecondary} onClick={handleOpenSettings}>
            ⚙️ Feedback Settings
          </button>
          {status === 'remind_later' && (
            <button style={S.btn} onClick={handleResendAll}>
              🔁 Resend All Reminders
            </button>
          )}
        </div>
      </div>

      {toast && (
        <div style={{
          padding: '14px 18px',
          borderRadius: 10,
          marginBottom: 20,
          fontSize: 13,
          fontWeight: 500,
          background: toast.type === 'success' ? '#ECFDF5' : '#FEF2F2',
          color: toast.type === 'success' ? '#065F46' : '#991B1B',
          border: `1.5px solid ${toast.type === 'success' ? '#A7F3D0' : '#FCA5A5'}`
        }}>
          {toast.message}
        </div>
      )}

      {/* Action and Filter Bar */}
      <div style={S.actionBar}>
        <input
          style={{ ...S.input, minWidth: 250 }}
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select style={S.select} value={role} onChange={e => handleRoleChange(e.target.value)}>
          <option value="">All Roles</option>
          <option value="buyer">Buyers</option>
          <option value="vendor">Vendors</option>
        </select>
        <select style={S.select} value={event} onChange={e => { setEvent(e.target.value); setPage(1); }}>
          <option value="">All Events</option>
          {(role === '' || role === 'buyer' || role === 'vendor') && (
            <option value="registration">Registration</option>
          )}
          {(role === '' || role === 'buyer') && (
            <option value="demand_posted">Demand Post</option>
          )}
          {(role === '' || role === 'vendor') && (
            <option value="offer_submitted">Offer Sent</option>
          )}
        </select>
        <select style={S.select} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="submitted">Submitted</option>
          <option value="remind_later">Reminded</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Main Card Table */}
      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>User</th>
              <th style={S.th}>Role</th>
              <th style={S.th}>Latest Event</th>
              <th style={S.th}>Latest Rating</th>
              <th style={S.th}>Latest Status</th>
              <th style={S.th}>Last Updated</th>
              <th style={S.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <tr key={idx}>
                  <td colSpan="7" style={{ padding: '8px 16px' }}><TableSkeleton rows={1} columns={1} showHeader={false} /></td>
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ padding: 24, textAlign: 'center' }}>
                  <EmptyState title="No feedback found" description="No user feedback entries match the current filter selection." />
                </td>
              </tr>
            ) : (
              data.map(item => {
                const latestFb = item.feedbacks[0];
                return (
                  <tr key={item.userId}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 700 }}>{item.user.fullName}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{item.user.email}</div>
                    </td>
                    <td style={S.td}>
                      <span style={{ textTransform: 'capitalize' }}>{item.user.role}</span>
                    </td>
                    <td style={S.td}>
                      {latestFb ? getEventName(latestFb.event) : '-'}
                    </td>
                    <td style={S.td}>
                      {latestFb?.rating ? (
                        <div style={S.starRating}>
                          <span>{'★'.repeat(latestFb.rating)}</span>
                          <span style={{ color: 'var(--muted)', fontSize: 12 }}>({latestFb.rating})</span>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--muted)' }}>No rating</span>
                      )}
                    </td>
                    <td style={S.td}>
                      {latestFb ? getStatusBadge(latestFb.status) : '-'}
                    </td>
                    <td style={S.td}>
                      {new Date(item.latestFeedback).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <button
                          style={S.iconBtn}
                          title="View all feedbacks"
                          onClick={() => handleViewHistory(item)}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          👁️
                        </button>
                        {latestFb?.status === 'remind_later' && (
                          <button
                            style={{ ...S.btn, padding: '6px 12px', fontSize: 12 }}
                            onClick={() => handleResend(latestFb._id)}
                          >
                            Send Again
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination footer */}
        {!loading && total > 15 && (
          <div style={S.pagination}>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>
              Showing {data.length} of {total} users
            </span>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                style={{ ...S.btnSecondary, padding: '6px 12px', fontSize: 12 }}
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <button
                style={{ ...S.btnSecondary, padding: '6px 12px', fontSize: 12 }}
                disabled={page * 15 >= total}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Dynamic Feedback Trigger Settings Modal */}
      {showSettings && (
        <div style={S.modalOverlay} onClick={() => setShowSettings(false)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowSettings(false)}
              style={{ position: 'absolute', top: 20, right: 20, background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--muted)' }}
            >
              ✕
            </button>
            <div style={S.modalTitle}>Feedback Dynamic Triggers</div>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>
              Define when feedbacks are dynamically requested from users instead of hardcoded intervals.
            </p>

            <form onSubmit={handleSaveSettings}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
                Buyer Settings
              </div>
              <div style={S.formRow}>
                <label style={S.label}>Registration Threshold (Days after signup)</label>
                <input
                  style={{ ...S.input, width: '100%' }}
                  type="number"
                  min="0"
                  value={settings.buyerRegDays}
                  onChange={e => setSettings({ ...settings, buyerRegDays: Number(e.target.value) })}
                  onWheel={e => e.target.blur()}
                  required
                />
              </div>
              <div style={S.formRow}>
                <label style={S.label}>Demand Threshold (Number of demands posted)</label>
                <input
                  style={{ ...S.input, width: '100%' }}
                  type="number"
                  min="0"
                  value={settings.buyerDemandCount}
                  onChange={e => setSettings({ ...settings, buyerDemandCount: Number(e.target.value) })}
                  onWheel={e => e.target.blur()}
                  required
                />
              </div>

              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, marginTop: 20, borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
                Vendor Settings
              </div>
              <div style={S.formRow}>
                <label style={S.label}>Registration Threshold (Days after signup)</label>
                <input
                  style={{ ...S.input, width: '100%' }}
                  type="number"
                  min="0"
                  value={settings.vendorRegDays}
                  onChange={e => setSettings({ ...settings, vendorRegDays: Number(e.target.value) })}
                  onWheel={e => e.target.blur()}
                  required
                />
              </div>
              <div style={S.formRow}>
                <label style={S.label}>Offer Threshold (Number of offers submitted)</label>
                <input
                  style={{ ...S.input, width: '100%' }}
                  type="number"
                  min="0"
                  value={settings.vendorOfferCount}
                  onChange={e => setSettings({ ...settings, vendorOfferCount: Number(e.target.value) })}
                  onWheel={e => e.target.blur()}
                  required
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
                <button type="button" style={S.btnSecondary} onClick={() => setShowSettings(false)}>
                  Cancel
                </button>
                <button type="submit" style={S.btn} disabled={savingSettings}>
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Feedback History Modal */}
      {showHistory && (
        <div style={S.modalOverlay} onClick={() => setShowHistory(false)}>
          <div style={{ ...S.modalCard, maxWidth: 650 }} onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowHistory(false)}
              style={{ position: 'absolute', top: 20, right: 20, background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--muted)' }}
            >
              ✕
            </button>
            <div style={S.modalTitle}>Feedback History</div>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>
              Chronological log of feedback interactions for <strong>{selectedUser?.fullName}</strong> ({selectedUser?.email})
            </p>

            <div style={{ maxHeight: 350, overflowY: 'auto', paddingRight: 6 }}>
              {loadingHistory ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--muted)' }}>
                  Loading history...
                </div>
              ) : userHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--muted)' }}>
                  No feedback history recorded.
                </div>
              ) : (
                userHistory.map(fb => (
                  <div key={fb._id} style={S.historyRow}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        Trigger Event: {getEventName(fb.event)}
                      </div>
                      <div>
                        {getStatusBadge(fb.status)}
                      </div>
                    </div>
                    {fb.status === 'submitted' && (
                      <div style={{ marginBottom: 6 }}>
                        <div style={S.starRating}>
                          <span>{'★'.repeat(fb.rating)}</span>
                          <span style={{ color: 'var(--muted)', fontSize: 12 }}>({fb.rating} stars)</span>
                        </div>
                        {fb.description && (
                          <div style={{ fontSize: 13, background: '#f9fafb', padding: '10px 14px', borderRadius: 8, marginTop: 6, fontStyle: 'italic', borderLeft: '3px solid #4C1D95' }}>
                            "{fb.description}"
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: 'var(--muted)' }}>
                      <span>Prompt Date: {new Date(fb.lastPromptedAt).toLocaleString()}</span>
                      {fb.status === 'remind_later' && (
                        <button
                          style={{ ...S.btn, padding: '4px 10px', fontSize: 11 }}
                          onClick={() => handleResend(fb._id)}
                        >
                          Resend Prompt
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
              <button style={S.btnSecondary} onClick={() => setShowHistory(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
